import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { Container } from "react-bootstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { DateTime } from "luxon";
import { BsFillPassFill } from "react-icons/bs";
import Select from "react-select";
import { useAuth } from "../context/AuthContext";
import { AxisDateWithDay } from "../util/AxisDateTime";
import AxisSpinner from "../components/AxisSpinner";
import { AvailabilityNewModal } from "../modals/AvailabilityNewModal";
import { AvailabilityDayModal } from "../modals/AvailabilityDayModal";
import { PageTitleController } from "../components/PageTitleController";
import DropDownTrustSelection from "../components/DropDownTrustSelection";
import { MedicalRolePills } from "../components/MedicalRolePills";
import {
  AvailabilityTypePills,
  availabilityAbbreviations,
} from "../components/AvailabilityTypePills";
import AvailabilityCardsExport from "../components/AvailabilityCardsExport";

const DAYS_PER_PAGE = 15;
const AVAILABILITY_RANGE_DAYS = 60;
const ROLE_OPTIONS = [
  "RN",
  "RM",
  "RMN",
  "RNLD",
  "RSCN",
  "HCA2",
  "HCA3",
  "LDOC",
  "LCON",
  "Support worker",
  "Radiographer",
  "Physiotherapist",
  "Lab assistant",
  "Bio-medical scientist",
  "Speech therapist",
  "Occupational therapist",
  "Pharmacist",
  "N/A",
].map((role) => ({ value: role, label: role }));

const AvailabilityCards = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [selectedTrust, setSelectedTrust] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [dateRange] = useState(() => {
    const today = DateTime.now().startOf("day");
    return {
      startDate: today.toISODate(),
      endDate: today.plus({ days: AVAILABILITY_RANGE_DAYS }).toISODate(),
    };
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const [showAddAvailability, setShowAddAvailability] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState(null);
  const pageHeaderMsg = null;

  const paginate = useCallback(async (pageToLoad = currentPage) => {
    try {
      setLoading(true);

      const response = await authAxios().post("/api/backoffice/availability/cards/paging", {
        ...dateRange,
        trustId: selectedTrust?.id || null,
        role: selectedRole?.value || null,
        page: pageToLoad,
        daysPerPage: DAYS_PER_PAGE,
      });

      setDataSource(response.data.content || []);
      setTotalDays(response.data.totalElements || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  }, [authAxios, currentPage, dateRange, selectedRole, selectedTrust]);

  useEffect(() => {
    document.title = "Availability Cards";
  }, []);

  useEffect(() => {
    paginate();
  }, [paginate]);

  useEffect(() => {
    if (showDayModal) return undefined;

    const timer = setInterval(() => {
      paginate();
    }, 10000);
    return () => clearInterval(timer);
  }, [paginate, showDayModal]);

  const closeDayModal = () => {
    setSelectedDate(null);
    setSelectedAvailabilityId(null);
    setShowDayModal(false);
    paginate();
  };

  const setTrustFilter = (trust) => {
    setSelectedTrust(trust);
    setCurrentPage(0);
  };

  const setRoleFilter = (role) => {
    setSelectedRole(role);
    setCurrentPage(0);
  };

  const openDayModal = (date, availabilityId = null) => {
    setSelectedDate(date);
    setSelectedAvailabilityId(availabilityId);
    setShowDayModal(true);
  };

  const formatAvailabilityAbbreviations = (availability) => {
    return availabilityAbbreviations(availability);
  };

  const formatAvailabilityName = (availability) => {
    const forename = availability?.user?.forename || "";
    const surname = availability?.user?.surname || "";

    return `${forename} ${surname}`.trim();
  };

  const formatAvailabilityLine = (availability) => {
    const userRole = availability?.user?.roles || "N/A";
    const availabilityTypes = formatAvailabilityAbbreviations(availability);
    return `${formatAvailabilityName(availability)} - ${userRole}${availabilityTypes ? ` - ${availabilityTypes}` : ""}`;
  };

  const formatAvailabilityNote = (availability) => {
    return availability?.user?.availabilityNote?.trim() || "";
  };

  const spinnerOrData = () => {
    if (loading && dataSource.length === 0) {
      return <AxisSpinner loading={loading} />;
    }

    if (dataSource.length === 0) {
      return (
        <Card className={"availability-card availability-day-card"}>
          <Card.Body>
            <div className={"text-axis-offblack"}>
              No availability found from today onward for the selected filters.
            </div>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Row xs={1} sm={2} lg={3} xl={4} className={"g-3"}>
        {dataSource.map((dayAvailability) => (
          <Col key={dayAvailability.date}>
            <Card
              className={
                "availability-card availability-day-card availability-day-card-clickable h-100"
              }
              onClick={() => openDayModal(dayAvailability.date)}
              style={{ cursor: "pointer" }}
            >
              <Card.Body>
                <Card.Title className={"text-axis-offblack mb-3"}>
                  {AxisDateWithDay(dayAvailability.date)}
                </Card.Title>

                <div className={"availability-card-list"}>
                  {dayAvailability.availability.map((availability) => (
                    <button
                      key={availability?.id}
                      type="button"
                      className={"availability-card-user"}
                      onClick={(event) => {
                        event.stopPropagation();
                        openDayModal(dayAvailability.date, availability?.id);
                      }}
                    >
                      <div className={"availability-card-user-line"}>
                        <span className={"availability-card-user-text"}>
                          {formatAvailabilityName(availability)}
                        </span>
                        <MedicalRolePills roles={availability?.user?.roles || "N/A"} />
                        <span className={"availability-card-user-separator"}>-</span>
                        <AvailabilityTypePills
                          availability={availability}
                        />
                        {formatAvailabilityNote(availability) !== "" && (
                          <span className={"availability-card-user-note"}>
                            {formatAvailabilityNote(availability)}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Fragment>
      {pageHeaderMsg && (
        <Row className={pageHeaderMsg.className}>{pageHeaderMsg.msg}</Row>
      )}

      <Row className={"icon-text-wrapper"}>
        <PageTitleController
          title={"Availability Cards"}
          icon={<BsFillPassFill className={"icon-right-spacing"} />}
        />
        <Col>
          <Row xs={"auto"} className={"justify-content-end mb-3 me-2 g-2"}>
            <Col>
              <AvailabilityCardsExport
                dateRange={dateRange}
                trust={selectedTrust}
                role={selectedRole}
                disabled={totalDays === 0}
              />
            </Col>
            <Col>
              <Button
                variant="offblack"
                onClick={() => setShowAddAvailability(true)}
              >
                + New Availability
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <div
        className="alert alert-success mb-3"
        role={"alert"}
        style={{ "--bs-alert-bg": "#d4edda" }}
      >
        App users can set their own availability up to {AxisDateWithDay(dateRange.endDate)} ({AVAILABILITY_RANGE_DAYS} days)
      </div>

      <Container fluid className={"m-0 p-0 mb-4"}>
        <Row xs={1} md={"auto"} className={"align-items-center g-3"}>
          <Col md={4} lg={3}>
            <DropDownTrustSelection
              onSelected={setTrustFilter}
              defaultValue={selectedTrust}
              placeholderText={"Filter by trust"}
            />
          </Col>
          <Col md={4} lg={3}>
            <Select
              className={"fs-input"}
              isClearable={true}
              options={ROLE_OPTIONS}
              value={selectedRole}
              onChange={setRoleFilter}
              placeholder={"Filter by role"}
            />
          </Col>
        </Row>
      </Container>

      {spinnerOrData()}

      {totalDays > 0 && (
        <div className={"mt-3"}>
          Page {currentPage + 1} of {Math.ceil(totalDays / DAYS_PER_PAGE)}, showing {dataSource.length} day card(s) out of {totalDays}
          <PaginationControl
            page={currentPage + 1}
            between={3}
            total={totalDays}
            limit={DAYS_PER_PAGE}
            changePage={(page) => setCurrentPage(page - 1)}
            ellipsis={1}
          />
        </div>
      )}

      <AvailabilityNewModal
        close={setShowAddAvailability}
        visible={showAddAvailability}
        forceUpdate={paginate}
      />
      <AvailabilityDayModal
        visible={showDayModal}
        close={closeDayModal}
        date={selectedDate}
        initialAvailabilityId={selectedAvailabilityId}
        availability={
          dataSource.find((day) => day.date === selectedDate)
            ?.availability || []
        }
        formatAvailabilityLine={formatAvailabilityLine}
        forceUpdate={paginate}
      />
    </Fragment>
  );
};

export default AvailabilityCards;
